import React, { useState, useEffect, useRef } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConnectorIcon from "./ConnectorIcon";
import GupshupTemplates from "./GupshupTemplates";
import { byType } from "./catalog";
import { useMenuStyles } from "../../components/menuStyles";

// Config inicial ao ABRIR pra criar: pré-preenche campos com `default` e a
// Base URL quando o placeholder já é uma URL fixa/padrão (sem SEU/SUA/<…>).
// É só um ponto de partida — o usuário pode alterar.
const cleanUrl = ph => typeof ph === "string" && /^https?:\/\/\S+$/i.test(ph) && !/(SEU|SUA|<|>|\.\.\.|\s)/.test(ph);
const initialConfig = cat => {
  const cfg = {};
  (cat.fields || []).forEach(f => {
    if (f.default !== undefined) cfg[f.name] = f.default;
    else if (f.name === "baseUrl" && cleanUrl(f.placeholder)) cfg[f.name] = f.placeholder;
  });
  return cfg;
};

// Configure a connector. `item` = catalog entry (new), `connector` = existing
// record (edit). On save it's persisted; the parent moves it to "Meus".
const ConnectorModal = ({ open, item, connector, onClose, onSaved }) => {
  const menu = useMenuStyles();
  const cat = item || (connector && byType(connector.type));
  const [name, setName] = useState("");
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [onboarding, setOnboarding] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  // WhatsApp (QRCode): pareamento por QR
  const [pairing, setPairing] = useState(false);
  const [waStatus, setWaStatus] = useState(null);
  const [waQr, setWaQr] = useState(null);
  const [waConnId, setWaConnId] = useState(null);
  const [waPhone, setWaPhone] = useState(null);
  const [egress, setEgress] = useState(null); // {fixed, ip} do plano (só MySQL)
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open || !cat || cat.type !== "mysql") { setEgress(null); return undefined; }
    let live = true;
    api.get("/connectors/mysql-egress").then(({ data }) => { if (live) setEgress(data); }).catch(() => {});
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cat && cat.type]);

  useEffect(() => {
    if (!open) return;
    setName(connector ? connector.name : cat ? cat.name : "");
    setConfig(connector && connector.config ? { ...connector.config } : cat ? initialConfig(cat) : {});
    setTestResult(null);
    setWaConnId(connector ? connector.id : null);
    setWaStatus(null);
    setWaQr(null);
    setWaPhone(connector && connector.config ? connector.config.phone : null);
    setPairing(false);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // stop polling when the modal unmounts/closes
  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  if (!cat) return null;

  const isGupshup = cat.type === "gupshup";
  const isWaQr = cat.type === "whatsapp_qr";
  const connected = config.status === "active";
  // WhatsApp (QRCode): conectado agora = sessão aberta (poll) ou já ativa no config
  const waConnectedNow = waStatus === "open" || (!waStatus && config.status === "active");

  const setField = (k, v) => setConfig(c => ({ ...c, [k]: v }));

  // WhatsApp (Gupshup) onboarding: open the hosted Embedded Signup popup, then
  // poll until our callback flips the connector to active.
  const connectWhatsApp = async () => {
    setOnboarding(true);
    try {
      // editando → reconecta ESTE; novo → outra conexão (a conta pode ter vários números)
      const { data } = await api.post(`/connectors/gupshup/onboard`, { name, connectorId: connector ? connector.id : undefined });
      if (data.embedUrl) {
        window.open(data.embedUrl, "gupshup_signup", "width=600,height=760");
      }
      const id = data.connectorId;
      let tries = 0;
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        tries += 1;
        try {
          const { data: list } = await api.get(`/connectors`);
          const c = ((list && list.connectors) || []).find(x => x.id === id);
          if (c && c.config && c.config.status === "active") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setOnboarding(false);
            toast.success("WhatsApp conectado!");
            onSaved(c);
          }
        } catch (e) {
          /* keep polling */
        }
        if (tries > 75) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setOnboarding(false);
        }
      }, 4000);
    } catch (err) {
      toastError(err);
      setOnboarding(false);
    }
  };

  // WhatsApp (QRCode): parear por QR. Inicia a sessão, mostra o QR e faz poll do
  // status até virar "open" (pareado). Espelha o fluxo do onboard do Gupshup.
  const startWaPoll = id => {
    if (pollRef.current) clearInterval(pollRef.current);
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/connectors/whatsapp-qr/status`, { params: { connectorId: id } });
        setWaStatus(data.status);
        if (data.qr) setWaQr(data.qr);
        if (data.status === "open") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPairing(false);
          setWaPhone(data.phone);
          setWaQr(null);
          toast.success("WhatsApp conectado!");
          try {
            const { data: list } = await api.get(`/connectors`);
            const c = (list.connectors || []).find(x => x.id === id);
            if (c) onSaved(c);
          } catch (e) {
            /* ignore */
          }
        }
      } catch (e) {
        /* keep polling */
      }
      if (tries > 100) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setPairing(false);
      }
    }, 3000);
  };

  const pairWhatsApp = async () => {
    setPairing(true);
    setWaQr(null);
    setWaStatus("connecting");
    try {
      const { data } = await api.post(`/connectors/whatsapp-qr/start`, {
        name: name || cat.name,
        connectorId: connector ? connector.id : waConnId,
      });
      setWaConnId(data.connectorId);
      if (data.qr) setWaQr(data.qr);
      if (data.status) setWaStatus(data.status);
      startWaPoll(data.connectorId);
    } catch (err) {
      toastError(err);
      setPairing(false);
    }
  };

  const disconnectWa = async () => {
    const id = connector ? connector.id : waConnId;
    if (!id) return;
    try {
      await api.post(`/connectors/whatsapp-qr/logout`, { connectorId: id });
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      setWaStatus("idle");
      setWaPhone(null);
      setWaQr(null);
      setPairing(false);
      toast.success("WhatsApp desconectado");
      try {
        const { data: list } = await api.get(`/connectors`);
        const c = (list.connectors || []).find(x => x.id === id);
        if (c) onSaved(c);
      } catch (e) {
        /* ignore */
      }
    } catch (err) {
      toastError(err);
    }
  };

  const testConn = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/connectors/test`, { type: cat.type, config, connectorId: connector && connector.id });
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, message: "Falha ao testar" });
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      let created = connector;
      if (connector) {
        const { data } = await api.put(`/connectors/${connector.id}`, { name, config });
        created = data;
      } else {
        const { data } = await api.post(`/connectors`, { type: cat.type, name, config });
        created = data;
      }
      toast.success("Conector salvo");
      onSaved(created);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ className: menu.dialogPaper }}>
      <DialogTitle>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <ConnectorIcon icon={cat.icon} name={cat.name} color={cat.color} size={36} />
          <span>
            {connector ? "Editar" : "Configurar"} {cat.name}
          </span>
        </div>
        {cat.description && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
            {cat.description}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Nome do conector"
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Como ele aparece em 'Meus conectores'"
        />
        {isGupshup ? (
          <div style={{ marginTop: 8 }}>
            <Typography
              variant="body2"
              style={{ marginBottom: 12, color: connected ? "#1E7E34" : undefined, fontWeight: 600 }}
            >
              {connected
                ? `✓ WhatsApp conectado${config.source ? ` (${config.source})` : ""}`
                : "Conecte o WhatsApp do cliente. Abre a tela oficial do Meta para login no Facebook, escolha do número e código de ativação."}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={onboarding ? <CircularProgress size={18} color="inherit" /> : <WhatsAppIcon />}
              onClick={connectWhatsApp}
              disabled={onboarding}
            >
              {onboarding
                ? "Aguardando conexão…"
                : connected
                ? "Reconectar WhatsApp"
                : "Conectar WhatsApp"}
            </Button>
            {onboarding && (
              <Typography variant="caption" display="block" style={{ marginTop: 8, color: "#777" }}>
                Conclua o cadastro na janela do Meta. Esta tela atualiza sozinha quando o número for ativado.
              </Typography>
            )}
            {config.appId && (
              <Button
                style={{ marginTop: 16, display: "block" }}
                variant="outlined"
                onClick={() => setShowTemplates(true)}
              >
                Gerenciar templates
              </Button>
            )}
          </div>
        ) : isWaQr ? (
          <div style={{ marginTop: 8 }}>
            {waConnectedNow ? (
              <>
                <Typography variant="body2" style={{ marginBottom: 12, color: "#1E7E34", fontWeight: 600 }}>
                  ✓ WhatsApp conectado{waPhone ? ` (${waPhone})` : ""}
                </Typography>
                <Button variant="outlined" startIcon={<WhatsAppIcon />} onClick={pairWhatsApp} disabled={pairing} style={{ marginRight: 8 }}>
                  {pairing ? "Gerando QR…" : "Parear outro número"}
                </Button>
                <Button variant="outlined" color="secondary" onClick={disconnectWa}>
                  Desconectar
                </Button>
              </>
            ) : (
              <>
                <Typography variant="body2" style={{ marginBottom: 12, fontWeight: 600 }}>
                  Pareie o WhatsApp escaneando o QR Code com o celular.
                </Typography>
                {!waQr && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={pairing ? <CircularProgress size={18} color="inherit" /> : <WhatsAppIcon />}
                    onClick={pairWhatsApp}
                    disabled={pairing}
                  >
                    {pairing ? "Gerando QR…" : "Parear WhatsApp"}
                  </Button>
                )}
                {waQr && (
                  <div style={{ textAlign: "center", marginTop: 8 }}>
                    <img
                      src={waQr}
                      alt="QR Code do WhatsApp"
                      style={{ width: 260, height: 260, border: "1px solid #E6E9ED", borderRadius: 12, padding: 8, background: "#fff" }}
                    />
                    <Typography variant="caption" display="block" style={{ marginTop: 8, color: "#777" }}>
                      No celular: WhatsApp → Aparelhos conectados → Conectar aparelho, e aponte para o QR. Esta tela atualiza sozinha.
                    </Typography>
                  </div>
                )}
                <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 10, background: "#FFFAEB", border: "1px solid #FEDF89" }}>
                  <div style={{ fontSize: 12.5, color: "#8A5A1B", lineHeight: 1.5 }}>
                    ⚠️ WhatsApp não oficial viola os Termos do WhatsApp — há risco de <b>bloqueio do número</b>. Use um chip dedicado.
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            {cat.type === "mysql" && egress && (egress.fixed ? (
              <div style={{ margin: "6px 0 12px", padding: "12px 14px", borderRadius: 10, background: "#ECFDF3", border: "1px solid #ABEFC6" }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "#067647" }}>🔒 Plano Pro — IP fixo de saída</div>
                <div style={{ fontSize: 12.5, color: "#3A6A50", margin: "6px 0 8px", lineHeight: 1.5 }}>
                  Libere este IP no firewall do seu banco — as consultas sempre sairão dele:
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontFamily: "monospace", fontSize: 15, fontWeight: 700, color: "#054F31", background: "#D1FADF", borderRadius: 8, padding: "6px 12px" }}>{egress.ip}</span>
                  <button type="button" onClick={() => { if (navigator.clipboard) navigator.clipboard.writeText(egress.ip); toast.success("IP copiado!"); }}
                    style={{ border: "1px solid #ABEFC6", background: "#fff", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: "#067647" }}>Copiar</button>
                </div>
              </div>
            ) : (
              <div style={{ margin: "6px 0 12px", padding: "12px 14px", borderRadius: 10, background: "#FFFAEB", border: "1px solid #FEDF89" }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "#B54708" }}>IP de saída dinâmico</div>
                <div style={{ fontSize: 12.5, color: "#8A5A1B", marginTop: 6, lineHeight: 1.5 }}>
                  No seu plano as conexões saem por IPs dinâmicos — o banco precisa ficar acessível pela internet. Caso precise de um <b>IP fixo</b>, faça{" "}
                  <a
                    href="/plans"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#B54708", fontWeight: 700, textDecoration: "underline", whiteSpace: "nowrap" }}
                  >
                    upgrade pro Pro
                    <OpenInNewIcon style={{ fontSize: 13, verticalAlign: "-2px", marginLeft: 2 }} />
                  </a>.
                </div>
              </div>
            ))}
            {cat.fields.map(f => (
              <TextField
                key={f.name}
                label={f.label}
                placeholder={f.placeholder || ""}
                type={f.secret ? "password" : "text"}
                value={config[f.name] || ""}
                onChange={e => setField(f.name, e.target.value)}
                fullWidth
                margin="normal"
              />
            ))}
          </>
        )}
        {testResult && (
          <Typography
            variant="body2"
            style={{
              marginTop: 8,
              color: testResult.ok ? "#1E7E34" : "#B12A2A",
              fontWeight: 600,
            }}
          >
            {testResult.ok ? "✓ " : "✕ "}
            {testResult.message}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{isGupshup || isWaQr ? "Fechar" : "Cancelar"}</Button>
        {(isGupshup || isWaQr) && connector && (
          <Button onClick={save} variant="contained" color="primary" disabled={saving || !name}>
            Salvar nome
          </Button>
        )}
        {!isGupshup && !isWaQr && (
          <>
            <Button onClick={testConn} disabled={testing || (cat.type === "mysql" ? !(config.host && config.user && config.database && (config.password || (connector && connector.hasPassword))) : !config.baseUrl)}>
              {testing ? "Testando…" : "Testar"}
            </Button>
            <Button
              onClick={save}
              variant="contained"
              color="primary"
              disabled={saving || !name}
            >
              Salvar
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
    {isGupshup && (
      <GupshupTemplates
        open={showTemplates}
        connectorId={connector && connector.id}
        onClose={() => setShowTemplates(false)}
      />
    )}
    </>
  );
};

export default ConnectorModal;
